import { PropAsset, PropRenderContext } from './PropAsset';
import { ImagePropAsset, ImagePropConfig } from './ImagePropAsset';
import { TreePine } from './TreePine';
import { TreeOak } from './TreeOak';
import { TreeDead } from './TreeDead';
import { Rock } from './Rock';
import { Bush } from './Bush';
import { Tent } from './Tent';
import { House } from './House';
import { Well } from './Well';
import { Barrel } from './Barrel';
import { Crate } from './Crate';
import { Cart } from './Cart';
import { Torch } from './Torch';
import { Sign } from './Sign';
import { Campfire } from './Campfire';
import { Grave } from './Grave';
import { Ruins } from './Ruins';
import { Statue } from './Statue';
import { Wall, WallCorner } from './Wall';
import { Stairs, StairsDown } from './Stairs';
import { Elevation, Cliff, Pillar } from './Elevation';
import { Fence, FenceCorner } from './Fence';
import { Gate, WallGate } from './Gate';
import { Bridge, Ramp } from './Bridge';
import { debugWarn } from '@shared/utils/Logger';

export type PropType =
  | 'tree_pine'
  | 'tree_oak'
  | 'tree_dead'
  | 'rock'
  | 'bush'
  | 'tent'
  | 'house'
  | 'well'
  | 'barrel'
  | 'crate'
  | 'cart'
  | 'torch'
  | 'sign'
  | 'grave'
  | 'ruins'
  | 'statue'
  | 'campfire'
  | 'wall'
  | 'wall_corner'
  | 'stairs'
  | 'stairs_down'
  | 'elevation'
  | 'cliff'
  | 'pillar'
  | 'fence'
  | 'fence_corner'
  | 'gate'
  | 'wall_gate'
  | 'bridge'
  | 'ramp';

// Singleton registry for all prop assets
class PropRegistryClass {
  private assets: Map<PropType, PropAsset> = new Map();
  private imageProps: ImagePropAsset[] = [];
  private currentScale: number = 1;

  constructor() {
    this.registerAll();
  }

  private registerAll(): void {
    // Trees
    this.register(new TreePine());
    this.register(new TreeOak());
    this.register(new TreeDead());

    // Nature
    this.register(new Rock());
    this.register(new Bush());

    // Structures
    this.register(new Tent());
    this.register(new House());
    this.register(new Well());

    // Objects
    this.register(new Barrel());
    this.register(new Crate());
    this.register(new Cart());
    this.register(new Torch());
    this.register(new Sign());
    this.register(new Campfire());

    // Dark/Sacred
    this.register(new Grave());
    this.register(new Ruins());
    this.register(new Statue());

    // Walls & Architecture
    this.register(new Wall());
    this.register(new WallCorner());
    this.register(new Pillar());
    this.register(new Fence());
    this.register(new FenceCorner());
    this.register(new Gate());
    this.register(new WallGate());

    // Stairs & Elevation
    this.register(new Stairs());
    this.register(new StairsDown());
    this.register(new Elevation());
    this.register(new Cliff());
    this.register(new Ramp());
    this.register(new Bridge());
  }

  private register(asset: PropAsset): void {
    this.assets.set(asset.id as PropType, asset);
  }

  // Get an asset by type
  get(type: PropType): PropAsset | undefined {
    return this.assets.get(type);
  }

  // Reusable render context (avoids per-prop object allocation)
  private _renderCtx: PropRenderContext = { ctx: null as unknown as CanvasRenderingContext2D, x: 0, y: 0, scale: 1, variant: undefined };

  // Render a prop at screen position
  render(
    ctx: CanvasRenderingContext2D,
    type: PropType,
    x: number,
    y: number,
    scale: number,
    variant?: number
  ): void {
    const asset = this.assets.get(type);
    if (!asset) {
      debugWarn('Props', `Unknown prop type "${type}"`);
      return;
    }

    const context = this._renderCtx;
    context.ctx = ctx;
    context.x = x;
    context.y = y;
    context.scale = scale;
    context.variant = variant;

    asset.render(context);
  }

  // Update scale and clear caches if changed significantly
  setScale(scale: number): void {
    const roundedScale = Math.round(scale * 10) / 10;
    const roundedCurrent = Math.round(this.currentScale * 10) / 10;

    if (roundedScale !== roundedCurrent) {
      this.clearAllCaches();
      this.currentScale = scale;
    }
  }

  // Clear all asset caches
  clearAllCaches(): void {
    for (const asset of this.assets.values()) {
      asset.clearCache();
    }
  }

  // Get all registered prop types
  getAllTypes(): PropType[] {
    return Array.from(this.assets.keys());
  }

  // Check if a prop type exists
  has(type: PropType): boolean {
    return this.assets.has(type);
  }

  // Initialize image-based props, replacing procedural ones with sprite versions
  initImageProps(): void {
    const configs: ImagePropConfig[] = [
      // Trees
      {
        id: 'tree_pine',
        width: 55, height: 100,
        walkable: false, collisionRadius: 0.8, occludesPlayer: true,
        imagePaths: ['/sprites/props/tree_pine_0.png'],
      },
      {
        id: 'tree_oak',
        width: 65, height: 95,
        walkable: false, collisionRadius: 0.9, occludesPlayer: true,
        imagePaths: ['/sprites/props/tree_oak_0.png'],
      },
      {
        id: 'tree_dead',
        width: 48, height: 88,
        walkable: false, collisionRadius: 0.6, occludesPlayer: true,
        imagePaths: ['/sprites/props/tree_dead_0.png'],
      },
      // Nature
      {
        id: 'rock',
        width: 40, height: 32,
        walkable: false, collisionRadius: 0.5,
        imagePaths: ['/sprites/props/rock_0.png'],
      },
      {
        id: 'bush',
        width: 32, height: 26,
        walkable: false, collisionRadius: 0.3,
        imagePaths: ['/sprites/props/bush_0.png'],
      },
      // Structures
      {
        id: 'house',
        width: 120, height: 130,
        walkable: false, collisionRadius: 2.0, occludesPlayer: true,
        imagePaths: ['/sprites/props/house_0.png'],
      },
      {
        id: 'tent',
        width: 70, height: 65,
        walkable: false, collisionRadius: 1.2, occludesPlayer: true,
        imagePaths: ['/sprites/props/tent_0.png'],
      },
      {
        id: 'well',
        width: 45, height: 55,
        walkable: false, collisionRadius: 0.7,
        imagePaths: ['/sprites/props/well_0.png'],
      },
      // Objects
      {
        id: 'barrel',
        width: 22, height: 28,
        walkable: false, collisionRadius: 0.4,
        imagePaths: ['/sprites/props/barrel_0.png'],
      },
      {
        id: 'crate',
        width: 24, height: 28,
        walkable: false, collisionRadius: 0.4,
        imagePaths: ['/sprites/props/crate_0.png'],
      },
      {
        id: 'campfire',
        width: 32, height: 40,
        walkable: false, collisionRadius: 0.5,
        imagePaths: ['/sprites/props/campfire_0.png'],
      },
      // Dark/Sacred
      {
        id: 'grave',
        width: 28, height: 32,
        walkable: false, collisionRadius: 0.4,
        imagePaths: ['/sprites/props/grave_0.png'],
      },
      {
        id: 'ruins',
        width: 85, height: 70,
        walkable: false, collisionRadius: 1.5, occludesPlayer: true,
        imagePaths: ['/sprites/props/ruins_0.png'],
      },
      {
        id: 'statue',
        width: 42, height: 72,
        walkable: false, collisionRadius: 0.6, occludesPlayer: true,
        imagePaths: ['/sprites/props/statue_0.png'],
      },
    ];

    for (const config of configs) {
      const existing = this.assets.get(config.id as PropType);
      if (existing) {
        config.fallback = existing;
      }
      const imageProp = new ImagePropAsset(config);
      this.assets.set(config.id as PropType, imageProp);
      this.imageProps.push(imageProp);
    }
  }

  // Preload all image props and wait for them to finish
  async preloadImageProps(): Promise<void> {
    await Promise.all(this.imageProps.map(p => p.waitForLoad()));
  }
}

// Export singleton instance
export const PropRegistry = new PropRegistryClass();
